import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { formatMoney, currencyForCountry } from "../lib/finance";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Target, Trash2, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const T = {
  fr: {
    title: "Mon objectif rêve",
    subtitle: "Donnez un nom à votre projet et voyez la progression.",
    empty_title: "Rêvez un peu — quel est votre projet ?",
    empty_body:
      "Un voyage, un cadeau aux petits-enfants, une réserve pour la retraite… Nommez-le et suivez la progression mois après mois.",
    set_cta: "Choisir mon objectif",
    name_label: "Mon objectif s'appelle",
    name_ph: "Voyage au Portugal, cadeau petits-enfants…",
    amount_label: "Montant total à atteindre",
    date_label: "Pour quand ? (facultatif)",
    save: "Enregistrer",
    cancel: "Annuler",
    saved: "Objectif enregistré !",
    delete_confirm: "Effacer cet objectif ?",
    saved_amount: "économisé sur",
    remaining: "Il reste",
    reached: "Bravo, objectif atteint !",
    per_month_needed: "Rythme actuel",
    per_month_actual: "€/mois",
    months_left: "mois",
    edit: "Modifier",
    remove: "Effacer",
  },
  en: {
    title: "My dream objective",
    subtitle: "Name your project and watch your progress.",
    empty_title: "Dream a little — what's your project?",
    empty_body:
      "A trip, a gift for the grandkids, a retirement cushion… Name it and track your progress month after month.",
    set_cta: "Pick my goal",
    name_label: "My goal is called",
    name_ph: "Trip to Portugal, gift for grandkids…",
    amount_label: "Total amount to reach",
    date_label: "By when? (optional)",
    save: "Save",
    cancel: "Cancel",
    saved: "Goal saved!",
    delete_confirm: "Erase this goal?",
    saved_amount: "saved out of",
    remaining: "Remaining",
    reached: "Bravo, goal reached!",
    per_month_needed: "Current pace",
    per_month_actual: "/month",
    months_left: "months",
    edit: "Edit",
    remove: "Remove",
  },
};

export default function ObjectiveCard() {
  const { user, refresh } = useAuth();
  const { lang } = useLang();
  const tr = T[lang] || T.fr;
  const currency = currencyForCountry(user?.country || "FR");

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.objective?.name || "");
  const [amount, setAmount] = useState(user?.objective?.amount || "");
  const [date, setDate] = useState(user?.objective?.target_date || "");
  const [busy, setBusy] = useState(false);

  const objective = user?.objective;
  // Progress = number of validated months * monthly savings (a fair heuristic)
  const monthly = user?.profile?.monthly_savings || 0;
  const checkinsCount = (user?.checkins || []).length;
  const saved = monthly * checkinsCount;
  const pct = objective
    ? Math.min(100, Math.round((saved / objective.amount) * 100))
    : 0;
  const remaining = objective ? Math.max(0, objective.amount - saved) : 0;
  const monthsLeft =
    objective && monthly > 0 ? Math.ceil(remaining / monthly) : null;

  const startEdit = () => {
    setName(objective?.name || "");
    setAmount(objective?.amount || "");
    setDate(objective?.target_date || "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    if (!name.trim() || !amount) return;
    setBusy(true);
    try {
      await api.put("/profile/objective", {
        name: name.trim(),
        amount: parseFloat(amount),
        target_date: date || null,
      });
      await refresh();
      setEditing(false);
      toast.success(tr.saved);
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(tr.delete_confirm)) return;
    setBusy(true);
    try {
      await api.delete("/profile/objective");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  // --- Edit / Create form ---
  if (editing) {
    return (
      <div
        className="rounded-3xl border-2 border-[#A762B1]/30 bg-gradient-to-br from-[#F5F0FF] to-[#FFF7E9] p-6 sm:p-8"
        data-testid="objective-form"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/20 text-[#8E4799]">
            <Target className="h-6 w-6" />
          </div>
          <h3 className="font-serif text-2xl font-semibold text-[#101A42]">
            {tr.title}
          </h3>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="obj-name" className="mb-2 block text-base font-medium">
              {tr.name_label}
            </Label>
            <Input
              id="obj-name"
              data-testid="objective-name-input"
              value={name}
              placeholder={tr.name_ph}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="h-14 rounded-xl border-2 text-base"
            />
          </div>
          <div>
            <Label htmlFor="obj-amount" className="mb-2 block text-base font-medium">
              {tr.amount_label}
            </Label>
            <Input
              id="obj-amount"
              data-testid="objective-amount-input"
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-14 rounded-xl border-2 text-base"
            />
          </div>
          <div>
            <Label htmlFor="obj-date" className="mb-2 block text-base font-medium">
              {tr.date_label}
            </Label>
            <Input
              id="obj-date"
              data-testid="objective-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-14 rounded-xl border-2 text-base"
            />
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={save}
              disabled={busy || !name.trim() || !amount}
              data-testid="objective-save-btn"
              className="min-h-[52px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#8E4799]"
            >
              {tr.save}
            </Button>
            <Button
              variant="outline"
              onClick={cancel}
              data-testid="objective-cancel-btn"
              className="min-h-[52px] rounded-full border-2 border-[#101A42] px-6 text-lg font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
            >
              {tr.cancel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- Empty state ---
  if (!objective) {
    return (
      <div
        className="rounded-3xl border-2 border-dashed border-[#A762B1]/40 bg-gradient-to-br from-[#F5F0FF] to-[#FFF7E9] p-6 sm:p-8 text-center"
        data-testid="objective-empty"
      >
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/20 text-[#8E4799]">
          <Target className="h-7 w-7" />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-[#101A42]">
          {tr.empty_title}
        </h3>
        <p className="mx-auto mt-2 max-w-md text-base leading-relaxed text-[#4A4A4A]">
          {tr.empty_body}
        </p>
        <Button
          onClick={startEdit}
          data-testid="objective-set-btn"
          className="mt-5 min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#8E4799]"
        >
          <PlusCircle className="mr-2 h-5 w-5" />
          {tr.set_cta}
        </Button>
      </div>
    );
  }

  // --- Objective progress view ---
  const reached = pct >= 100;
  return (
    <div
      className="rounded-3xl border-2 border-[#A762B1]/30 bg-gradient-to-br from-[#F5F0FF] to-[#FFF7E9] p-6 sm:p-8"
      data-testid="objective-progress"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
              reached
                ? "bg-gradient-to-br from-[#E59D32] to-[#F4C77B] text-[#5B4200]"
                : "bg-[#A762B1]/20 text-[#8E4799]"
            }`}
          >
            <Target className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#8E4799]">
              {tr.title}
            </p>
            <h3
              className="font-serif text-2xl font-semibold text-[#101A42]"
              data-testid="objective-name"
            >
              {objective.name}
            </h3>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={startEdit}
            data-testid="objective-edit-btn"
            className="text-sm text-[#101A42]"
          >
            {tr.edit}
          </Button>
          <Button
            variant="ghost"
            onClick={remove}
            disabled={busy}
            data-testid="objective-delete-btn"
            className="text-sm text-[#A762B1]"
          >
            <Trash2 className="mr-1 h-4 w-4" />
            {tr.remove}
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-lg text-[#101A42]" data-testid="objective-saved">
            <strong>{formatMoney(saved, currency, lang)}</strong> {tr.saved_amount}{" "}
            <strong>{formatMoney(objective.amount, currency, lang)}</strong>
          </span>
          <span
            className={`font-serif text-3xl font-semibold ${
              reached ? "text-[#B8860B]" : "text-[#8E4799]"
            }`}
            data-testid="objective-pct"
          >
            {pct} %
          </span>
        </div>
        <Progress value={pct} className="h-3" data-testid="objective-bar" />
        {reached ? (
          <p
            className="mt-4 rounded-xl bg-[#E59D32]/15 p-3 text-lg font-semibold text-[#8A6300]"
            data-testid="objective-reached"
          >
            ✨ {tr.reached}
          </p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-white/60 p-3">
              <p className="text-sm text-[#4A4A4A]">{tr.remaining}</p>
              <p className="font-serif text-xl font-semibold text-[#101A42]">
                {formatMoney(remaining, currency, lang)}
              </p>
            </div>
            {monthsLeft && monthly > 0 && (
              <div className="rounded-xl bg-white/60 p-3">
                <p className="text-sm text-[#4A4A4A]">
                  {tr.per_month_needed} ({formatMoney(monthly, currency, lang)}
                  {tr.per_month_actual})
                </p>
                <p className="font-serif text-xl font-semibold text-[#101A42]">
                  ~{monthsLeft} {tr.months_left}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
